<template>
  <div class="min-w-0">
    <with-error-message :show-error="isXmlFeedUrlError">
      <span class="flex">
        <base-label id="url" label="XML Feed URL" :label-classes="'font-medium'"/>
        <vs-tooltip class="flex ml-1 items-center"
                    position="bottom"
                    @mouseenter.native="clearHideTimeout"
                    @mouseleave.native="setHideTimeout"
                    ref="xmlFeedBestPracticesRef"
        >
          <template #content>
            <div v-html="xmlFeedBestPracticesHint"/>
          </template>

          <vs-icon icon="info" class="text-gray cursor-default"/>
        </vs-tooltip>
      </span>
      <base-input id="url"
                  name="url"
                  placeholder="e.g. https://www.website.com/your-feed.xml"
                  variant="editor"
                  type="string"
                  class="w-full border-gray-border py-px mt-1 h-10"
                  :class="{'border-focus-error': isXmlFeedUrlError}"
                  :value="url"
                  @input="updateUrl"
      />
    </with-error-message>

    <span class="text-black font-semibold mt-5 flex">
      DEFINE XML ELEMENTS
      <vs-tooltip class="flex ml-1 items-center" position="bottom">
        <template #content>
          <div v-html="xmlElementsHint"/>
        </template>

        <vs-icon icon="info" class="text-gray cursor-default"/>
      </vs-tooltip>
    </span>

    <div class="flex flex-col mt-1">
      <with-error-message :show-error="isItemTagError" class="w-1/2 pr-2">
        <base-label id="item" label="Item Tag" :label-classes="'font-medium'"/>
        <base-input id="item"
                    name="item"
                    placeholder="<item-tag>"
                    variant="editor"
                    type="string"
                    class="w-full border-gray-border py-px mt-1 h-10"
                    :class="{'border-focus-error': isItemTagError}"
                    :value="item"
                    @input="updateItem"
                    @blur="stripAngleBrackets('item')"
        />
      </with-error-message>

      <div class="flex w-full gap-4 mt-3">
        <with-error-message :show-error="isItemNameTagError" class="w-1/2">
          <base-label id="itemName" label="Item Name Tag" :label-classes="'font-medium'"/>
          <base-select v-if="availableTags.length > 0"
                      :value="itemName"
                      :options="availableTags"
                      class="w-full h-10 mt-1"
                      @input="updateItemName"
          />
          <base-input v-else
                      id="itemName"
                      name="itemName"
                      placeholder="<item-name-tag>"
                      variant="editor"
                      type="string"
                      class="w-full border-gray-border py-px mt-1 h-10"
                      :class="{'border-focus-error': isItemNameTagError}"
                      :value="itemName"
                      @input="updateItemName"
                      @blur="stripAngleBrackets('itemName')"
          />
        </with-error-message>

        <with-error-message :show-error="isItemIdTagError" class="w-1/2">
          <base-label id="itemId" label="Item ID Tag" :label-classes="'font-medium'"/>
          <base-select v-if="availableTags.length > 0"
                      :value="itemId"
                      :options="availableTags"
                      class="w-full h-10 mt-1"
                      @input="updateItemId"
          />
          <base-input v-else
                      id="itemId"
                      name="itemId"
                      placeholder="<item-id-tag>"
                      variant="editor"
                      type="string"
                      class="w-full border-gray-border py-px mt-1 h-10"
                      :class="{'border-focus-error': isItemIdTagError}"
                      :value="itemId"
                      @input="updateItemId"
                      @blur="stripAngleBrackets('itemId')"
          />
        </with-error-message>
      </div>
    </div>

    <p v-if="previewError" class="text-red-600 text-sm mt-3">{{ previewError }}</p>

    <settings-index-field-selection v-if="showFieldSelection && availableTags.length"
                                    :available-tags="availableTags"
                                    :field-examples="itemTagData || {}"
                                    :item-tag="item"
                                    :value="fieldSelection"
                                    class="mt-5"
                                    @input="$emit('update:field-selection', $event)"
    />
  </div>
</template>

<script>
import WithErrorMessage from '@/components/common/WithErrorMessage.vue'
import SettingsIndexFieldSelection from './SettingsIndexFieldSelection.vue'
import previewModule from '@/store/modules/preview.module'
import utilsService from '@/services/amio/utils.service'

export default {
  name: 'SettingsIndexesXmlFeed',
  components: { WithErrorMessage, SettingsIndexFieldSelection },
  props: {
    url: String,
    item: String,
    itemName: String,
    itemId: String,
    showFieldSelection: {
      type: Boolean,
      default: false
    },
    fieldSelection: {
      type: Object,
      default: () => ({ embed_fields: [], payload_fields: {}, group_key: [] })
    }
  },
  data() {
    return {
      hideTimeout: null,
      itemTagData: null,
      isLoadingPreview: false,
      cachedUrl: null,
      previewError: null
    }
  },
  watch: {
    // surface the previewed feed fields to the parent so Qdrant field selection can use them
    availableTags: {
      immediate: true,
      handler(tags) {
        this.$emit('update:available-tags', tags)
      }
    },
    url: {
      immediate: true,
      handler(newUrl, oldUrl) {
        // Only fetch if URL actually changed
        // On initial mount (oldUrl is undefined), only fetch if URL is provided
        if (oldUrl === undefined) {
          if (newUrl) {
            this.fetchXmlPreview()
          }
        } else if (newUrl !== oldUrl) {
          this.fetchXmlPreview()
        }
      }
    }
  },
  methods: {
    updateUrl(updatedUrl) {
      if (updatedUrl === this.url) return

      if (this.isXmlFeedUrlError) this.error = null
      this.$emit('update:url', updatedUrl)
    },

    async fetchXmlPreview() {
      if (!this.url || !this.url.trim() || this.url === this.cachedUrl) {
        return
      }

      this.isLoadingPreview = true
      this.previewError = null
      this.cachedUrl = this.url

      try {
        const result = await utilsService.previewXmlFeed(this.url, this.item)
        if (result) {
          this.updateItem(result.itemTag || '')
          this.itemTagData = result.itemTagData
          this.$nextTick(() => {
            if (!this.itemTagData[this.itemName]) this.updateItemName('')
            if (!this.itemTagData[this.itemId]) this.updateItemId('')
          })
        }
        this.previewError = null
      } catch (error) {
        this.previewError = error.message || 'Failed to load XML feed preview'
      } finally {
        this.isLoadingPreview = false
      }
    },
    stripAngleBrackets(field) {
      const value = this[field]
      if (!value) return

      const stripped = value.replace(/[<>]/g, '')
      if (stripped !== value) {
        const updateMap = {
          item: 'update:item',
          itemName: 'update:item-name',
          itemId: 'update:item-id'
        }
        this.$emit(updateMap[field], stripped)
      }
    },
    updateItem(updatedItem) {
      this.$emit('update:item', updatedItem)
    },
    updateItemName(updatedItemName) {
      this.$emit('update:item-name', updatedItemName)
    },
    updateItemId(updatedItemId) {
      this.$emit('update:item-id', updatedItemId)
    },
    setHideTimeout() {
      // HACK: persist the tooltip for user to click on link
      this.$refs.xmlFeedBestPracticesRef?.mouseenterx()
      this.hideTimeout = setTimeout(() => {
        this.$refs.xmlFeedBestPracticesRef?.mouseleavex()
      }, 1500)
    },
    clearHideTimeout() {
      clearTimeout(this.hideTimeout)
    }
  },
  computed: {
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,

    availableTags() {
      return this.itemTagData ? Object.keys(this.itemTagData).sort() : []
    },

    xmlElementsHint() {
      return `In order to correctly process the XML feed, we need to know which tags contain some specific product information.
        <br/><br/>
        Item Tag - name of tag that wraps all information of a single product.
        <br/><br/>
        Item Name Tag - name of tag that contains the product name.
        <br/><br/>
        Item ID Tag - name of tag that contains unique indentifier of the product.
      `
    },
    xmlFeedBestPracticesHint() {
      return `For best results, ensure that your feed follows <a href="https://docs.amio.io/docs/bot-product-feeds-best-practices" target="_blank">best practices</a>.
        <br/>The feed must be pre-generated (i.e. just-in-time generation will not be accepted).'
      `
    },
    isXmlFeedUrlError() {
      return this.errorDescription?.includes('URL') || this.errorDescription?.includes('File not found')
    },
    isItemTagError() {
      return this.errorDescription?.includes('Item Tag')
    },
    isItemNameTagError() {
      return this.errorDescription?.includes('Item Name Tag')
    },
    isItemIdTagError() {
      return this.errorDescription?.includes('Item ID Tag')
    }
  },
  destroyed() {
    if (this.isXmlFeedUrlError) this.error = null
  }
}
</script>
